import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { T, prettyType, sentenceCase, snakeWords, coerceJson, PrettyValue } from "./pdUtil";
import { IconChartDocuments } from "../RevqMenuIcons";

// SchemaRead — the data-driven Lens renderer for ANY document type that used a
// central/custom extraction template but isn't one of the few bespoke layouts
// (gov ID, insurance, encounter, lab, radiology, meds). It is driven ENTIRELY by
// the payload the analysis already carries — prompt_used (template identity),
// processing_metadata.field_priority_analysis (required / good-to-have tiers) and
// classification.indicator_profile — never by a hardcoded document_type switch.
// Core rule: no extracted field is ever dropped. Worst case the raw JSON is one tap away.

const META = new Set([
  "extraction_confidence", "extraction_failed", "extraction_warnings", "raw_text", "raw_text_full",
  "confidence", "should_process", "relevance", "indicator_profile", "reason", "document_title",
]);

const LONG = 80;

const TIER_LABEL = { required: "Required", core: "Required", good_to_have: "Recommended", recommended: "Recommended", optional: "Optional", additional: "Additional" };
const TIER_ORDER = ["required", "core", "good_to_have", "recommended", "optional", "additional"];

// Read the template's field-priority matrix (processing_metadata.field_priority_analysis).
// Confirmed shape: tiers[name] = { total, extracted, missing, missing_fields:[], ratio }.
// Returns per-tier coverage counts + the missing field names grouped by tier — never
// used to drop data, only to describe how completely the template was filled.
function readTiers(analysis) {
  const fpa = analysis?.processing_metadata?.field_priority_analysis || analysis?.field_priority_analysis;
  const t = fpa?.tiers || fpa;
  const coverage = []; const missingByTier = [];
  let extractedTotal = 0; let expectedTotal = 0;
  if (t && typeof t === "object" && !Array.isArray(t)) {
    const names = Object.keys(t).sort((a, b) => (TIER_ORDER.indexOf(a) + 1 || 99) - (TIER_ORDER.indexOf(b) + 1 || 99));
    for (const name of names) {
      const v = t[name];
      if (!v || typeof v !== "object" || v.total == null) continue;
      const label = TIER_LABEL[name] || sentenceCase(name);
      coverage.push({ label, extracted: v.extracted ?? 0, total: v.total });
      extractedTotal += v.extracted ?? 0; expectedTotal += v.total ?? 0;
      const mf = Array.isArray(v.missing_fields) ? v.missing_fields : [];
      if (mf.length) missingByTier.push({ label, fields: mf });
    }
  }
  const quality = fpa && typeof fpa === "object" ? (fpa.weighted_quality || null) : null;
  return { coverage, missingByTier, extractedTotal, expectedTotal, quality };
}

function splitFields(fields) {
  const simple = [], prose = [], tables = [], complex = [];
  fields.forEach(([k, v]) => {
    const c = coerceJson(v);
    if (Array.isArray(c) && c.some((x) => x && typeof x === "object")) tables.push([k, c]);
    else if (typeof c === "string" && c.length > LONG) prose.push([k, v]);
    else if (c && typeof c === "object") complex.push([k, c]);
    else simple.push([k, v]);
  });
  return { simple, prose, tables, complex };
}

const Kicker = ({ children }) => <div className="text-[11px] font-bold uppercase tracking-[.05em] mb-1.5" style={{ color: T.faint }}>{children}</div>;

function GenericTable({ name, rows }) {
  const cols = [...new Set(rows.flatMap((r) => (r && typeof r === "object" ? Object.keys(r) : [])))]
    .filter((k) => rows.some((r) => r && r[k] != null && r[k] !== ""));
  if (!cols.length) return <PrettyValue value={rows} />;
  const cell = (v) => {
    const c = coerceJson(v);
    if (c == null || c === "") return <span style={{ color: T.mute }}>—</span>;
    if (typeof c === "object") return <PrettyValue value={c} />;
    return snakeWords(c);
  };
  return (
    <div className="rounded-[10px] overflow-x-auto" style={{ border: `1px solid ${T.hair}` }} data-testid={`pd-schema-table-${name}`}>
      <table className="w-full" style={{ borderCollapse: "collapse", minWidth: cols.length > 3 ? 480 : undefined }}>
        <thead>
          <tr>{cols.map((k) => <th key={k} className="text-left text-[10.5px] uppercase tracking-[.04em] px-3 py-1.5" style={{ background: T.soft, color: T.mute, fontWeight: 700 }}>{sentenceCase(k)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${T.hair}` }}>
              {cols.map((k) => <td key={k} className="align-top px-3 py-1.5 text-[12.5px]" style={{ color: T.ink }}>{cell(r && r[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FieldGroup({ title, fields, testid }) {
  if (!fields.length) return null;
  const { simple, prose, tables, complex } = splitFields(fields);
  return (
    <div data-testid={testid}>
      {title ? <Kicker>{title}</Kicker> : null}
      {simple.length ? (
        <div className="grid gap-x-4 gap-y-2.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
          {simple.map(([k, v]) => (
            <div key={k} className="min-w-0" data-testid={`pd-schema-field-${k}`}>
              <div className="text-[11.5px] mb-0.5" style={{ color: T.faint }}>{sentenceCase(k)}</div>
              <div className="text-[13px] font-semibold break-words" style={{ color: T.ink }}>{snakeWords(v)}</div>
            </div>
          ))}
        </div>
      ) : null}
      {prose.length ? (
        <div className="space-y-3 mt-3">
          {prose.map(([k, v]) => (
            <div key={k} data-testid={`pd-schema-field-${k}`}>
              <div className="text-[11.5px] font-semibold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>{sentenceCase(k)}</div>
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap m-0" style={{ color: T.ink, maxWidth: "68ch" }}>{v}</p>
            </div>
          ))}
        </div>
      ) : null}
      {tables.map(([k, v]) => (
        <div key={k} className="mt-3" data-testid={`pd-schema-field-${k}`}>
          <div className="text-[11.5px] font-semibold uppercase tracking-[.04em] mb-1.5" style={{ color: T.faint }}>{sentenceCase(k)} · {v.length}</div>
          <GenericTable name={k} rows={v} />
        </div>
      ))}
      {complex.length ? (
        <div className="space-y-3 mt-3">
          {complex.map(([k, v]) => (
            <div key={k} data-testid={`pd-schema-field-${k}`}>
              <div className="text-[11.5px] font-semibold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>{sentenceCase(k)}</div>
              <PrettyValue value={v} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Fold({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-[12px] font-semibold py-1" style={{ color: T.faint }}>
        <ChevronRight size={13} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} />{title}
      </button>
      {open ? <div className="pl-4 pb-2">{children}</div> : null}
    </div>
  );
}

export default function SchemaRead({ analysis }) {
  const ex = analysis?.extraction || {};
  const docType = analysis?.document_type || analysis?.classification?.document_type;
  const prompt = analysis?.prompt_used || {};
  const promptName = prompt.prompt_name || analysis?.prompt_name;
  const promptId = prompt.prompt_id || analysis?.prompt_id;
  const isCustom = (prompt.is_custom_prompt ?? analysis?.is_custom_prompt) === true;
  const hasTemplate = !!(promptId || promptName);
  const label = promptName || prettyType(docType);
  const title = ex.document_title || label;

  const { extractedTotal, expectedTotal, quality } = readTiers(analysis);

  const present = []; const empty = [];
  for (const [k, v] of Object.entries(ex)) {
    if (META.has(k)) continue;
    if (v == null || v === "" || (Array.isArray(v) && !v.length)) empty.push(k);
    else present.push([k, v]);
  }

  // Prefer the template's own expected/extracted counts when the matrix provides them.
  const readCount = expectedTotal > 0
    ? `${extractedTotal} of ${expectedTotal} template fields read`
    : `${present.length} field${present.length === 1 ? "" : "s"} extracted`;

  return (
    <div className="space-y-4" data-testid="pd-schema-read">
      <div>
        <div className="flex items-center gap-2 mb-1.5" data-testid="pd-schema-template-badge">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[.05em] rounded-[7px] px-2 py-[3px]" style={{ background: hasTemplate ? "#EEF0FF" : T.soft, color: hasTemplate ? T.indigo : T.mute }}>
            <IconChartDocuments size={12} />{hasTemplate ? "Template" : "No template"}
          </span>
          <span className="text-[12.5px] font-semibold" style={{ color: T.sub }}>{label}</span>
          {quality ? <span className="text-[10.5px] font-bold uppercase tracking-[.05em] rounded-[6px] px-1.5 py-[2px]" style={{ background: T.soft, color: T.mute }} data-testid="pd-schema-quality">{snakeWords(quality)}</span> : null}
        </div>
        <h3 className="text-[18px] font-bold m-0" style={{ color: T.ink }}>{title}</h3>
        <div className="text-[12px] mt-0.5" style={{ color: T.mute }}>
          {hasTemplate
            ? `Read with the ${isCustom ? "custom " : ""}${label} template · ${readCount}`
            : `Read without a template · ${readCount}`}
        </div>
      </div>

      <FieldGroup title={null} fields={present} testid="pd-schema-group-all" />

      <div className="pt-1 space-y-1" style={{ borderTop: `1px solid ${T.hair}` }}>
        {empty.length ? (
          <Fold title={`Lumina Lens can also read these additional fields for this template if available · ${empty.length}`} testid="pd-schema-fold-empty">
            <div className="flex flex-wrap gap-1.5">{empty.map((k) => <span key={k} className="text-[11.5px]" style={{ color: T.mute }}>{sentenceCase(k)}</span>)}</div>
          </Fold>
        ) : null}
      </div>
    </div>
  );
}
